/**
 * @file Per-app configuration for Google Ads / Google Ads Dataset.
 * Uses an existing `google_ads` credential to call `get_accessible_accounts`,
 * then patches `custom_data` on the credential being configured.
 *
 * If the user has no `google_ads` credential, the panel prompts them to add one.
 * The google_ads_dataset Fivetran connector expects `custom_data.customer_id`.
 *
 * Renders either as a post-OAuth modal (`variant="dialog"`, the default) or
 * inline inside the Settings sub-tab (`variant="inline"`), where it pre-loads
 * the saved account and keeps Save disabled until the selection changes.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Loader2, Plus } from "lucide-react";
import { ANALYTICS_EVENTS, useTrack } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@strivelabs/ui-core";
import { executeAppAction, BASE_URL } from "@/services/DataService";
import { useCredentials, usePatchCredentialCustomData } from "@/hooks/useConnections";
import { logger } from "@/lib/logger";
import { InlineConfigPanel } from "@/components/settings/InlineConfigPanel";
import type { ConnectionsCardData, PostConnectionConfig } from "@/types/connections";

interface GoogleAdsConfigDialogProps {
  /** Modal open state — used by the dialog variant only. */
  open?: boolean;
  config: PostConnectionConfig;
  onComplete: () => void;
  onConnectGoogleAds?: () => void;
  /** 'dialog' (default) renders a modal; 'inline' renders an embedded panel. */
  variant?: "dialog" | "inline";
}

interface GoogleAdsAccount {
  id: string;
  name: string;
  manager: boolean;
}

/** Shape of a single account from the Google Ads get_accessible_accounts response. */
interface GoogleAdsAccountRaw {
  id: string;
  descriptive_name?: string;
  manager?: boolean;
}

/** Shape of the executeAppAction response for Google Ads. */
interface GoogleAdsActionResponse {
  status: string;
  items: Array<{
    scenario: string;
    data: GoogleAdsAccountRaw;
  }>;
}

/**
 * Parses the get_accessible_accounts response into a list of top-level accounts.
 */
function parseAccountsResponse(response: unknown): GoogleAdsAccount[] {
  const typed = response as GoogleAdsActionResponse;
  const items = typed?.items;

  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const data = item?.data;
      if (!data || typeof data.id !== "string") return null;

      return {
        id: data.id,
        name: data.descriptive_name ?? data.id,
        manager: data.manager ?? false,
      };
    })
    .filter((account): account is GoogleAdsAccount => account !== null);
}

export function GoogleAdsConfigDialog({
  open,
  config,
  onComplete,
  onConnectGoogleAds,
  variant = "dialog",
}: GoogleAdsConfigDialogProps) {
  const isInline = variant === "inline";
  const trackEvent = useTrack();
  // The form is "active" (fetches data) whenever the modal is open, or always
  // for the inline panel (which only mounts when its sub-tab is shown).
  const active = isInline || !!open;

  const { data: credentialsData, isLoading: isLoadingCredentials, isSuccess: credentialsLoaded } = useCredentials();

  /** All google_ads credentials available to the user. */
  const googleAdsCredentials = useMemo(
    () =>
      (credentialsData?.credentials ?? []).filter(
        (cred: ConnectionsCardData) => cred.app_name === "google_ads"
      ) as ConnectionsCardData[],
    [credentialsData]
  );

  /** The currently-saved account id — Save stays disabled until the selection differs from it. */
  const savedCustomerId = useMemo(() => {
    const cred = (credentialsData?.credentials ?? []).find(
      (c: ConnectionsCardData) => c.id === config.credentialId
    );
    const customData = cred?.custom_data ?? {};
    const id = config.appName === "google_ads" ? customData.account_id : customData.customer_id;
    return typeof id === "string" ? id : "";
  }, [credentialsData, config.credentialId, config.appName]);

  const [selectedGoogleAdsCredId, setSelectedGoogleAdsCredId] = useState<string>("");
  const [accounts, setAccounts] = useState<GoogleAdsAccount[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patchCustomData = usePatchCredentialCustomData();

  /** Auto-select the only google_ads credential when there's exactly one. */
  useEffect(() => {
    if (active && googleAdsCredentials.length === 1 && !selectedGoogleAdsCredId) {
      setSelectedGoogleAdsCredId(googleAdsCredentials[0].id);
    }
  }, [active, googleAdsCredentials, selectedGoogleAdsCredId]);

  const fetchAccounts = useCallback(async (credId: string) => {
    if (!credId) return;

    setLoading(true);
    setError(null);
    try {
      const res = await executeAppAction(
        BASE_URL,
        "google_ads",
        "get_accessible_accounts",
        [{
          id: credId
        }]
      );
      const parsed = parseAccountsResponse(res);
      if (parsed.length === 0) {
        setError("No accounts found. Please check your Google Ads access.");
      }
      setAccounts(parsed);
    } catch (err) {
      logger(err as Error, { context: "Google Ads config: Error loading accounts" });
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('insufficient') || message.includes('scope') || message.includes('403') || message.includes('401')) {
        setError("Insufficient permissions. Please re-authorize your Google Ads account and try again. If the issue persists, contact support.");
      } else {
        setError("Failed to load accounts. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /** Dialog only: reset state when the modal opens. */
  useEffect(() => {
    if (open && !isInline) {
      setSelectedGoogleAdsCredId("");
      setSelectedCustomerId("");
      setAccounts([]);
      setError(null);
    }
  }, [open, isInline]);

  /** Inline: pre-load the saved selection once the credential data is available. */
  const initedRef = useRef(false);
  useEffect(() => {
    if (!isInline || initedRef.current || !credentialsLoaded) return;
    initedRef.current = true;
    // Seed the saved account so the panel opens on the current selection; it
    // renders once the accounts list resolves and survives that refetch.
    setSelectedCustomerId(savedCustomerId);
    // When configuring a google_ads connection itself, use it as the source.
    if (config.appName === "google_ads") {
      setSelectedGoogleAdsCredId(config.credentialId);
    }
  }, [isInline, credentialsLoaded, savedCustomerId, config.appName, config.credentialId]);

  /** Fetch accounts whenever the selected google_ads credential changes. */
  useEffect(() => {
    if (active && selectedGoogleAdsCredId) {
      // Dialog mode clears the selection on cred change; inline keeps the
      // seeded/saved account so it survives the accounts refetch.
      if (!isInline) setSelectedCustomerId("");
      setAccounts([]);
      fetchAccounts(selectedGoogleAdsCredId);
    }
  }, [active, isInline, selectedGoogleAdsCredId, fetchAccounts]);

  const handleAddGoogleAdsConnection = () => {
    onConnectGoogleAds?.();
  };

  const handleSave = async () => {
    try {
      const selectedAccount = accounts.find((a) => a.id === selectedCustomerId);
      const isGoogleAds = config.appName === 'google_ads';
      await patchCustomData.mutateAsync({
        credentialId: config.credentialId,
        customData: isGoogleAds
          ? {
              account_id: selectedCustomerId,
              account_name: selectedAccount?.name ?? selectedCustomerId,
            }
          : {
              customer_id: selectedCustomerId,
              customer_name: selectedAccount?.name ?? selectedCustomerId,
            },
      });

      toast({
        title: "Configuration saved",
        description: `${config.displayName} has been configured with account "${selectedAccount?.name ?? selectedCustomerId}".`,
      });
      trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONFIGURED, {
        provider: config.appName,
        config_type: "app_config",
      });
      onComplete();
    } catch (err) {
      logger(err as Error, { context: "Google Ads config: Error saving custom data" });
      toast({
        title: "Error",
        description: "Failed to save configuration. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Enabled only when an account is chosen and it differs from what's saved.
  const canSave = selectedCustomerId !== "" && selectedCustomerId !== savedCustomerId;
  const hasNoGoogleAdsCredential = !isLoadingCredentials && googleAdsCredentials.length === 0;
  const hasMultipleGoogleAdsCredentials = googleAdsCredentials.length > 0;

  const body = (
    <>
      {/* Step 1: Google Ads credential selection */}
      {isLoadingCredentials ? (
        <div className="flex items-center gap-2 py-4 justify-center text-sm text-custom-text-primary opacity-50">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading credentials...
        </div>
      ) : hasNoGoogleAdsCredential ? (
        <div className="space-y-3 py-2">
          <p className="text-sm text-custom-text-primary opacity-70">
            A Google Ads connection is required to fetch your Google Ads accounts.
            Please add one to continue.
          </p>
          <Button variant="outline" size="sm" onClick={handleAddGoogleAdsConnection}>
            <Plus className="h-4 w-4 mr-2" />
            Add Google Ads Connection
          </Button>
        </div>
      ) : (
        <>
          {/* Show credential picker only when there are multiple google_ads credentials */}
          {hasMultipleGoogleAdsCredentials && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Google Ads Connection</Label>
              <Select value={selectedGoogleAdsCredId} onValueChange={setSelectedGoogleAdsCredId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a Google Ads connection" />
                </SelectTrigger>
                <SelectContent>
                  {googleAdsCredentials.map((cred) => (
                    <SelectItem key={cred.id} value={cred.id}>
                      {cred.display_name} — {cred.principal_name || cred.principal_email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Step 2: Google Ads account selection */}
          {selectedGoogleAdsCredId && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Google Ads Account</Label>
              {loading ? (
                <div className="flex items-center gap-2 py-4 justify-center text-sm text-custom-text-primary opacity-50">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading accounts...
                </div>
              ) : error ? (
                <div className="space-y-2 py-2">
                  <p className="text-sm text-red-500">{error}</p>
                  <Button variant="outline" size="sm" onClick={() => fetchAccounts(selectedGoogleAdsCredId)}>
                    Retry
                  </Button>
                </div>
              ) : (
                <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a Google Ads account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} ({account.id}){account.manager ? " — Manager" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </>
      )}
    </>
  );

  if (isInline) {
    return (
      <InlineConfigPanel
        title={`Configure ${config.displayName}`}
        description="Select the Google Ads account you want to sync data from."
        onSave={handleSave}
        canSave={canSave}
        saving={patchCustomData.isPending}
      >
        <div className="space-y-4">{body}</div>
      </InlineConfigPanel>
    );
  }

  return (
    <Dialog open={!!open} onOpenChange={(isOpen) => { if (!isOpen) onComplete(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Configure {config.displayName}</DialogTitle>
          <DialogDescription>
            Select the Google Ads account you want to sync data from.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">{body}</div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onComplete}>
            Skip
          </Button>
          <Button
            variant="black"
            onClick={handleSave}
            disabled={!canSave || patchCustomData.isPending}
          >
            {patchCustomData.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
